import { Formik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import {
  accountsApi,
  getWorksheet,
  insuranceApi,
  listAccountsByClass,
  patchWorksheet,
  transfersApi,
} from "@/features/worksheets/api";
import { AutosaveForm } from "@/features/worksheets/components/autosave-form";
import { CollectionCard, type Column } from "@/features/worksheets/components/collection-card";
import { AccountDialog } from "@/features/worksheets/components/dialogs/account-dialog";
import { InsuranceDialog } from "@/features/worksheets/components/dialogs/insurance-dialog";
import { TransferDialog } from "@/features/worksheets/components/dialogs/transfer-dialog";
import { MoneyField } from "@/features/worksheets/components/money-field";
import { useCollection } from "@/features/worksheets/hooks/use-collection";
import { centsToDollars, formatMoney } from "@/features/worksheets/money";
import type {
  AccountClass,
  AssetTransfer,
  FinancialAccount,
  InsurancePolicy,
} from "@/features/worksheets/types";

const TRANSFER_TYPE_LABEL: Record<AssetTransfer["transferType"], string> = {
  N: "Asset",
  R: "Real estate",
};

/** One account-class section: a CollectionCard backed by `listAccountsByClass`
 * plus an AccountDialog with the class preset. `extraColumns` adds the
 * class-specific column (key-access / quick-sale rate). */
function AccountSection({
  caseId,
  title,
  accountClass,
  extraColumns = [],
}: {
  caseId: string;
  title: string;
  accountClass: AccountClass;
  extraColumns?: Column<FinancialAccount>[];
}) {
  const list = useCallback((id: string) => listAccountsByClass(id, accountClass), [accountClass]);
  const { rows, loading, reload } = useCollection<FinancialAccount>(caseId, list);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<FinancialAccount | null>(null);

  const columns: Column<FinancialAccount>[] = [
    { header: "Institution", cell: (r) => r.institution || "—" },
    { header: "Type", cell: (r) => r.accountType || "—" },
    // Masked label only — never render the raw account number.
    { header: "Account #", cell: (r) => r.accountNumberLabel || "—" },
    { header: "Value", cell: (r) => formatMoney(r.accountValue), className: "text-right" },
    { header: "Loan", cell: (r) => formatMoney(r.loanBalance), className: "text-right" },
    ...extraColumns,
  ];

  return (
    <>
      <CollectionCard
        title={title}
        addLabel="Add Account"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => {
          setEditing(null);
          setOpen(true);
        }}
        onEdit={(row) => {
          setEditing(row);
          setOpen(true);
        }}
        onDelete={async (row) => {
          await accountsApi.remove(caseId, row.id);
          reload();
        }}
      />
      <AccountDialog
        caseId={caseId}
        accountClass={accountClass}
        record={editing}
        open={open}
        onOpenChange={setOpen}
        onSaved={reload}
      />
    </>
  );
}

function InsuranceSection({ caseId }: { caseId: string }) {
  const { rows, loading, reload } = useCollection<InsurancePolicy>(caseId, insuranceApi.list);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<InsurancePolicy | null>(null);

  const columns: Column<InsurancePolicy>[] = [
    { header: "Company", cell: (r) => r.company || "—" },
    { header: "Policy #", cell: (r) => r.policyNo || "—" },
    { header: "Cash Value", cell: (r) => formatMoney(r.cashValue), className: "text-right" },
    { header: "Loan", cell: (r) => formatMoney(r.loanBalance), className: "text-right" },
  ];

  return (
    <>
      <CollectionCard
        title="Life insurance"
        addLabel="Add Policy"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => {
          setEditing(null);
          setOpen(true);
        }}
        onEdit={(row) => {
          setEditing(row);
          setOpen(true);
        }}
        onDelete={async (row) => {
          await insuranceApi.remove(caseId, row.id);
          reload();
        }}
      />
      <InsuranceDialog
        caseId={caseId}
        record={editing}
        open={open}
        onOpenChange={setOpen}
        onSaved={reload}
      />
    </>
  );
}

function TransferSection({ caseId }: { caseId: string }) {
  const { rows, loading, reload } = useCollection<AssetTransfer>(caseId, transfersApi.list);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AssetTransfer | null>(null);

  const columns: Column<AssetTransfer>[] = [
    { header: "Type", cell: (r) => TRANSFER_TYPE_LABEL[r.transferType] },
    { header: "Description", cell: (r) => r.description || "—" },
    { header: "To Whom", cell: (r) => r.toWhom || "—" },
    { header: "Date", cell: (r) => r.transferDate || "—" },
    { header: "Amount", cell: (r) => formatMoney(r.amount), className: "text-right" },
  ];

  return (
    <>
      <CollectionCard
        title="Asset transfers"
        description="Assets and real estate transferred in the last 2 years."
        addLabel="Add Transfer"
        columns={columns}
        rows={rows}
        loading={loading}
        onAdd={() => {
          setEditing(null);
          setOpen(true);
        }}
        onEdit={(row) => {
          setEditing(row);
          setOpen(true);
        }}
        onDelete={async (row) => {
          await transfersApi.remove(caseId, row.id);
          reload();
        }}
      />
      <TransferDialog
        caseId={caseId}
        record={editing}
        open={open}
        onOpenChange={setOpen}
        onSaved={reload}
      />
    </>
  );
}

/** Cash on hand — a worksheet-header money field surfaced at the top of the
 * Banking tab and autosaved. */
function CashOnHandCard({ caseId }: { caseId: string }) {
  const [initial, setInitial] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getWorksheet(caseId)
      .then((ws) => active && setInitial(centsToDollars(ws.cashOnHand)))
      .catch(() => active && setInitial(""));
    return () => {
      active = false;
    };
  }, [caseId]);

  if (initial === null) return null;

  return (
    <Card className="p-4">
      <h3 className="mb-3 font-semibold">Cash on hand</h3>
      <Formik initialValues={{ cashOnHand: initial }} onSubmit={() => undefined} enableReinitialize>
        <div className="grid max-w-xs gap-4">
          <MoneyField name="cashOnHand" label="Cash on hand" />
          <AutosaveForm save={(patch) => patchWorksheet(caseId, patch)} moneyFields={["cashOnHand"]} />
        </div>
      </Formik>
    </Card>
  );
}

export function BankingTab({ caseId }: { caseId: string }) {
  return (
    <div className="grid gap-6">
      <CashOnHandCard caseId={caseId} />
      <AccountSection caseId={caseId} title="Personal accounts" accountClass="P" />
      <AccountSection caseId={caseId} title="Investment accounts" accountClass="I" />
      <AccountSection
        caseId={caseId}
        title="Virtual currency"
        accountClass="V"
        extraColumns={[
          { header: "Key access", cell: (r) => (r.keyAccess ? "Yes" : "No") },
        ]}
      />
      <AccountSection
        caseId={caseId}
        title="Retirement accounts"
        accountClass="R"
        extraColumns={[{ header: "Quick-sale rate", cell: (r) => (r.qsRate ? `${r.qsRate}%` : "—") }]}
      />
      <AccountSection caseId={caseId} title="Available credit / cards" accountClass="C" />
      <InsuranceSection caseId={caseId} />
      <TransferSection caseId={caseId} />
    </div>
  );
}
